import {useMutation, useSuspenseQuery} from "@apollo/client";
import {useState} from "react";
import {toast} from "react-hot-toast";

import {Alert} from "@/components/alert/Alert.tsx";
import {Card} from "@/components/card/Card.tsx";
import {CodeBlock} from "@/components/codeBlock/CodeBlock.tsx";
import {useCurrentUserContext} from "@/components/context/hooks.ts";
import {ActionDialog} from "@/components/overlay/ActionDialog.tsx";
import {getLink, PageLink} from "@/lib/links.ts";
import {GQL_API_TOKENS_GENERATE, GQL_API_TOKENS_QUERY} from "@/lib/queries/apitokens.ts";
import {ApiTokensResult, GenerateApiTokenResponse} from "@/lib/types/apitokens.ts";
import {ResponseStatus} from "@/lib/types/common.ts";

export const APITokensContent = () => {
  const {brand, user} = useCurrentUserContext();
  const {data} = useSuspenseQuery<ApiTokensResult>(GQL_API_TOKENS_QUERY);
  const result = data?.apiTokens || {};
  const apiKeys = result.keys || [];
  const subaccounts = result.subaccounts || [];
  const hasAPIAccess = user?.is_api_enabled && user?.account?.enable_api_access && apiKeys.length > 0;

  const [showModal, setShowModal] = useState(false);
  const [generateApiToken] = useMutation<GenerateApiTokenResponse>(GQL_API_TOKENS_GENERATE, {
    refetchQueries: [GQL_API_TOKENS_QUERY],
  });
  const submitModal = async () => {
    const response = await generateApiToken();
    const generateResult = response.data?.generateApiToken;
    if (generateResult?.status === ResponseStatus.SUCCESS) {
      setShowModal(false);
      toast.success("API key generated successfully");
    } else {
      toast.error(generateResult?.message.__all__[0] || "Failed to generate API key");
    }
  };

  const apiURL = getLink(brand, PageLink.BROWSABLE_API);
  const subaccountsURL = `${apiURL}auth/subaccounts/`;
  const dashboardsURL = `${apiURL}dashboards/`;

  return (
    <div>
      {hasAPIAccess ? (
        <>
          <div>
            <h5 className="heading fw-bold my-4">API Access</h5>
            <Card variant="white" className="mb-4">
              <div>
                <p>
                  To access our API programatically, you must pass one of the below API keys via a HTTP Header with a
                  key name of <code>"Authorization"</code> and a key prefix of <code>"Token"</code> with the below API
                  Key.
                </p>
                <div className="mb-2 fw-bold">Raw HTTP Example:</div>
                <CodeBlock
                  code={`GET /api/v1/\nAuthorization: Token %API_KEY%`}
                  codeWithSecret={`GET /api/v1/\nAuthorization: Token ${apiKeys[0]}`}
                />
                <div className="mb-2 fw-bold">CURL Example:</div>
                <CodeBlock
                  code={`curl -H "Authorization: Token %API_KEY%" ${apiURL}`}
                  codeWithSecret={`curl -H "Authorization: Token ${apiKeys[0]}" ${apiURL}`}
                  className="mb-0"
                />
              </div>
            </Card>
          </div>
          <div>
            <h5 className="heading fw-bold mb-4 mt-5">Your API Key</h5>
            <Card variant="white">
              <button
                className="btn btn-link text-decoration-none float-end ms-2 p-0"
                onClick={() => {
                  setShowModal(true);
                }}
              >
                Generate new key
              </button>
              <div className="mb-2">Please use the following API key in all requests:</div>
              {apiKeys.map((key, idx) => (
                <CodeBlock key={`apiKey-${idx}`} code="%API_KEY%" codeWithSecret={key} className="py-2 px-3 mb-1" />
              ))}
            </Card>
            <ActionDialog
              onCancel={() => setShowModal(false)}
              onConfirm={() => submitModal()}
              show={showModal}
              title={"Generate New API Key"}
            >
              Are you sure you want to create a new API key? Your current API key will be removed from the system and
              will no longer work for API calls.
            </ActionDialog>
          </div>
          {subaccounts.length > 0 && (
            <div>
              <h5 className="heading fw-bold mb-4 mt-5">Subaccounts</h5>
              <Card variant="white">
                <p>
                  Your account contains one or more subaccounts. In each API request you may specify which subaccount
                  you wish to operate on (or leave blank to use the main account.) To see a list of available
                  subaccounts and their IDs make an API request to{" "}
                  <a target="_blank" href={subaccountsURL}>
                    {subaccountsURL}
                  </a>
                  .
                </p>
                <p>
                  To select a subaccount provide either the <code>?subaccount=&lt;pk&gt;</code> query string parameter
                  or the <code>X-Subaccount: &lt;pk&gt;</code> HTTP header in your requests.
                </p>
                <p className="mb-2 fw-bold">HTTP header example:</p>
                <CodeBlock
                  code={`curl -H "X-Subaccount: ${subaccounts[0].id}" -H "Authorization: Token %API_KEY%" ${dashboardsURL}`}
                  codeWithSecret={`curl -H "X-Subaccount: ${subaccounts[0].id}" -H "Authorization: Token ${apiKeys[0]}" ${dashboardsURL}`}
                />
                <p className="mb-2 fw-bold">Query string example:</p>
                <CodeBlock
                  code={`curl -H "Authorization: Token %API_KEY%" ${dashboardsURL}?subaccount=${subaccounts[0].id}`}
                  codeWithSecret={`curl -H "Authorization: Token ${apiKeys[0]}" ${dashboardsURL}?subaccount=${subaccounts[0].id}`}
                  className="mb-0"
                />
              </Card>
            </div>
          )}
        </>
      ) : (
        <div>
          <h5 className="heading fw-bold my-4">API Access</h5>
          <Alert variant="error" className="mb-4">
            API access is currently disabled for your user account. To enable it, please reach out to your account
            administrator.
          </Alert>
        </div>
      )}
      <div>
        <h5 className="heading fw-bold mb-4 mt-5">API Fair Use Limits</h5>
        <Card variant="white">
          <p>
            To ensure high performance and fair access of the API for all our customers, API calls are rate-limited to a
            maximum of {result.rate_limit.hour} calls per hour and {result.rate_limit.minute} calls per minute. There
            are no daily or monthly usage limits.{" "}
          </p>
          If you require assistance in optimizing your API usage to fit within these limits, please{" "}
          <a href="mailto:support@uptime.com">contact support</a>.
        </Card>
      </div>
    </div>
  );
};
